import { useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Grid,
  Paper,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material'

import Button from '../components/Button'
import Dialog from '../components/Dialog'
import GridHeader from '../components/GridHeader'
import GridIconButton from '../components/GridIconButton'
import SelectionBar from '../components/selection/SelectionBar'
import SortableTableHead from '../components/SortableTableHead'
import GridCircularProgress from '../components/GridCircularProgress'
import DialogRichiestaArbitraggio from '../components/DialogRichiestaArbitraggio'
import { config } from '../settings/configs'
import {
  colours,
  titlesArbitraggi as titles,
  phrasesArbitraggi as phrases,
} from '../settings/settings'
import { selectArbitraggiSorted } from '../store/selectors'
import { fromYYYYMMDDtoDDMMYYYY } from '../hooks/utils-functions'
import { useFeedback } from '../hooks/feedback-hook'
import {
  useGetArbitraggiQuery,
  useCreateArbitraggioMutation,
  useDeleteArbitraggioMutation,
  useGetCalendariManyQuery,
  useGetIscrizioniQuery,
} from '../hooks/api-slice'

const Arbitraggi = () => {
  const { admin, designatore, codiceUtente } = useSelector(
    state => state.home.infoUtente,
  )
  const selectedCampionato = useSelector(
    state => state.selection.selectedCampionato,
  )
  const selectedCampionatoCalendariPubblicati = useSelector(
    state => state.selection.selectedCampionatoCalendariPubblicati,
  )

  const skipQueries = !(
    selectedCampionato &&
    (admin || selectedCampionatoCalendariPubblicati)
  )
  const { data: iscrizioniLight } = useGetIscrizioniQuery(
    { campionato: selectedCampionato, iFilter: 'light' },
    { skip: skipQueries },
  )
  const availableCategorie = useSelector(
    state => state.selection.availableCategorie,
  )
  const selectedCategoria = useSelector(
    state => state.selection.selectedCategoria,
  )

  const aArgs = { selectedCampionato, admin, designatore, codiceUtente }
  const { isLoading } = useGetArbitraggiQuery(aArgs, { skip: skipQueries })
  const arbitraggi = useSelector(state => selectArbitraggiSorted(state, aArgs))

  const idGare = arbitraggi?.map(a => a.idGara)
  const { data: calendari } = useGetCalendariManyQuery(idGare, {
    skip: !idGare?.length,
  })

  const [createArbitraggioMutation] = useCreateArbitraggioMutation()
  const [deleteArbitraggioMutation] = useDeleteArbitraggioMutation()

  document.title = `${config?.name} - Richieste di arbitraggio`

  const { setFeedback } = useFeedback()

  let columnsArbitraggi = [
    { label: 'Richiesto in data', sortable: false },
    { label: 'Gara', sortable: true, sortingField: 'numeroGara' },
    { label: 'Categoria', sortable: false },
    { label: 'Squadra in casa', sortable: false },
    { label: 'Squadra in trasferta', sortable: false },
  ]
  if (admin || designatore)
    columnsArbitraggi = [
      ...columnsArbitraggi,
      { label: 'Data della gara', sortable: false },
      { label: '', sortable: false },
    ]

  const [deletingItem, setDeletingItem] = useState()
  const [openDeleteArbitraggio, setOpenDelete] = useState()

  const handleClickOpenDeleteArbitraggio = s => {
    setDeletingItem(s)
    setOpenDelete(true)
  }

  const handleCloseDelete = () => {
    setOpenDelete()
    setDeletingItem()
  }

  const [openArbitraggioAdmin, setOpenArbitraggioAdmin] = useState()
  const handleClickOpenArbitraggioAdmin = () => setOpenArbitraggioAdmin(true)
  const handleCloseArbitraggioAdmin = () => setOpenArbitraggioAdmin()

  const arbitraggiN = arbitraggi?.filter(
    s =>
      s.categoria === selectedCategoria ||
      selectedCategoria === availableCategorie[0] ||
      !selectedCategoria,
  )

  const deleteArbitraggio = async () => {
    if (!deletingItem?.codiceCampionato || !deletingItem?.numeroGara) {
      setFeedback('Impossibile procedere: richiesta non trovata!')
      return
    }

    const result = await deleteArbitraggioMutation({
      codiceCampionato: deletingItem.codiceCampionato,
      numeroGara: deletingItem.numeroGara,
    })

    if (!result?.error) {
      handleCloseDelete()
      setFeedback(phrases.phraseDeleteSuccess, false, titles.titleDelete)
    }
  }

  const performArbitraggioAdmin = async selectedGare => {
    const idGara = selectedGare?.[0]

    if (!idGara) {
      setFeedback('Nessuna gara selezionata!')
      return
    }

    const result = await createArbitraggioMutation({ idGara })

    if (!result?.error) {
      handleCloseArbitraggioAdmin()
      setFeedback(phrases.phraseSuccess, false, titles.titleSuccess)
    }
  }

  return (
    <Grid container paddingX={2} spacing={2} sx={{ width: '100%' }}>
      <GridHeader h2 text='Richieste di arbitraggio' />
      <Grid container alignItems='center' sx={{ width: '100%' }}>
        <SelectionBar data={arbitraggi} yCampionato yCategoria />
        {admin && (
          <Grid size='grow'>
            <center>
              <Button onClick={handleClickOpenArbitraggioAdmin}>
                Nuova richiesta
              </Button>
            </center>
          </Grid>
        )}
      </Grid>
      {(isLoading && <GridCircularProgress />) ||
        (!arbitraggiN && (
          <GridHeader text='Elenco delle richieste di arbitraggio non trovato.' />
        )) ||
        (arbitraggiN.length === 0 && (
          <GridHeader text='Nessuna richiesta di arbitraggio trovata.' />
        )) || (
          <Grid container paddingY={2} sx={{ width: '100%' }}>
            <TableContainer component={Paper}>
              <Table size='small'>
                <SortableTableHead
                  table={arbitraggiN}
                  columns={columnsArbitraggi}
                  tableName='arbitraggi'
                />
                <TableBody>
                  {arbitraggiN.map((s, index) => {
                    const sData = fromYYYYMMDDtoDDMMYYYY(s.timeDomanda)
                    const sGara = calendari?.find(c => c._id === s.idGara)
                    const sCasa = iscrizioniLight?.find(
                      i => i.id === sGara?.squadraCasaID,
                    )?.nomeSquadra
                    const sTrasferta = iscrizioniLight?.find(
                      i => i.id === sGara?.squadraTrasfertaID,
                    )?.nomeSquadra

                    return (
                      <TableRow
                        key={index}
                        sx={{
                          ':hover': { backgroundColor: colours.greyVeryLight },
                        }}
                      >
                        <TableCell>{sData}</TableCell>
                        <TableCell>{s.numeroGara}</TableCell>
                        <TableCell>{s.categoria}</TableCell>
                        <TableCell>{sCasa}</TableCell>
                        <TableCell>{sTrasferta}</TableCell>
                        {(admin || designatore) && (
                          <TableCell>{sGara?.data}</TableCell>
                        )}
                        {(admin || designatore) && (
                          <TableCell>
                            <GridIconButton
                              type='delete'
                              onClick={() =>
                                handleClickOpenDeleteArbitraggio(s)
                              }
                            />
                          </TableCell>
                        )}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}
      <Dialog
        open={openDeleteArbitraggio}
        handleClose={handleCloseDelete}
        title={titles.titleDelete}
        textContent={phrases.phraseDelete}
        textUndo='Annulla'
        textConfirm='Ok'
        triggerFunction={deleteArbitraggio}
        error
      />
      <DialogRichiestaArbitraggio
        open={openArbitraggioAdmin}
        handleClose={handleCloseArbitraggioAdmin}
        triggerFunction={performArbitraggioAdmin}
      />
    </Grid>
  )
}

export default Arbitraggi
